import React, { useState, useEffect } from "react";
import { StyleSheet, View } from "react-native";
import { useAuth } from "@/hooks/auth";
import { layout } from "@/constants";
import {
  ScreenLayout,
  Text,
  TextButton,
  TextInput,
  NotificationCard,
  Title,
  ScrollView
} from "@/components";
import * as apiClient from "@/utils/serenity-api-client";
import { isAdmin } from "@/store/admins";

/**
 * Admin screen for managing the user allowlist
 */
export default function AdminScreen() {
  const { user, getIdToken } = useAuth();
  const [isAdminUser, setIsAdminUser] = useState<boolean | null>(null);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{
    variant: "info" | "error";
    label: string;
    message: string;
  } | null>(null);

  useEffect(() => {
    if (user?.uid) {
      isAdmin(user.uid).then(setIsAdminUser);
    }
  }, [user?.uid]);

  const handleAdd = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;

    try {
      setIsSubmitting(true);
      setStatus(null);
      const token = await getIdToken();
      if (!token) return;

      const result = await apiClient.addToAllowlist(trimmed, token);
      if (result.success) {
        setStatus({ variant: "info", label: "Added", message: trimmed });
        setEmail("");
      } else {
        setStatus({
          variant: "error",
          label: "Error",
          message: result.message
        });
      }
    } catch {
      setStatus({
        variant: "error",
        label: "Error",
        message: "Failed to add to allowlist"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isAdminUser === null) {
    return (
      <ScreenLayout>
        <ScrollView>
          <Title>Admin</Title>
          <Text color="secondary">Loading...</Text>
        </ScrollView>
      </ScreenLayout>
    );
  }

  if (!isAdminUser) {
    return (
      <ScreenLayout>
        <ScrollView>
          <Title>Admin</Title>
          <Text color="secondary">Access denied.</Text>
        </ScrollView>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout>
      <ScrollView>
        <Title subtitle="Add users to the allowlist.">Admin</Title>

        <View style={styles.form}>
          <TextInput
            style={styles.input}
            placeholder="Email address"
            value={email}
            onChangeText={setEmail}
            onSubmitEditing={handleAdd}
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
            editable={!isSubmitting}
          />
          {status && (
            <NotificationCard
              variant={status.variant}
              label={status.label}
              message={status.message}
            />
          )}
          <TextButton
            buttonText={isSubmitting ? "Adding..." : "Add to Allowlist"}
            onPress={handleAdd}
            disabled={isSubmitting || !email.trim()}
            accessibilityLabel="Add to allowlist"
          />
        </View>
      </ScrollView>
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  form: {
    marginTop: layout.space.xl,
    alignItems: "center",
    gap: layout.space.md
  },
  input: {
    width: "100%",
    maxWidth: 400
  }
});
